import { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
    NOTIFICATION_COLUMN_FILTERS,
    QueueFilter,
    SendType,
} from "./constants";

/**
 * Query-string key for automated vs manual send type (`{@link SendType}` values).
 * @constant {string}
 */
const URL_SEND_TYPE = "sendType";

/**
 * Query-string key for the queue status segment (values `all` | `pending` | `sent` | `errors`; maps to {@link QueueFilter}).
 * @constant {string}
 */
const URL_STATUS = "status";

/**
 * Compares two column-filter maps for shallow string equality on every key present in either object.
 * Missing keys are treated as empty strings so `{}` and `{ client: "" }` compare equal per key.
 *
 * @param {Record<string, string>} a First filter map (may be empty).
 * @param {Record<string, string>} b Second filter map (may be empty).
 * @returns {boolean} True when all overlapping keys have identical trimmed-value semantics (empty vs absent matches).
 */
function columnFiltersEqual(a, b) {
    const keys = new Set([
        ...Object.keys(a ?? {}),
        ...Object.keys(b ?? {}),
    ]);

    for (const k of keys) {
        const va = a?.[k] ?? "";
        const vb = b?.[k] ?? "";
        if (va !== vb) {
            return false;
        }
    }

    return true;
}

/**
 * Reads the notifications queue filter shape from the current route search string.
 *
 * - **`sendType`**: Uses {@link URL_SEND_TYPE}; only {@link SendType.MANUAL} selects manual; any other or missing value becomes {@link SendType.AUTOMATED}.
 * - **`status`**: Uses {@link URL_STATUS}; `pending` / `sent` / `errors` map to {@link QueueFilter}; `all`, empty, missing, or unknown values map to {@link QueueFilter.ALL} (`null`).
 * - **Column filters**: For each entry in {@link NOTIFICATION_COLUMN_FILTERS}, reads that query key; non-empty trimmed strings are stored; absent keys are omitted.
 *
 * @param {URLSearchParams} searchParams Current location search params (typically from `useSearchParams()`).
 * @returns {{
 *   sendType: string,
 *   queueFilter: string | null,
 *   columnFilters: Record<string, string>,
 * }} Parsed filter state ready for React state or API calls.
 */
function parseNotificationsQueueSearchParams(searchParams) {
    const rawSend = searchParams.get(URL_SEND_TYPE)?.toLowerCase().trim();
    const sendType =
        rawSend === SendType.MANUAL ? SendType.MANUAL : SendType.AUTOMATED;

    const rawStatus = searchParams.get(URL_STATUS)?.toLowerCase().trim();
    let queueFilter = QueueFilter.ALL;

    if (rawStatus === "sent") {
        queueFilter = QueueFilter.SENT;
    } else if (rawStatus === "pending") {
        queueFilter = QueueFilter.PENDING;
    } else if (rawStatus === "errors") {
        queueFilter = QueueFilter.ERRORS;
    } else if (
        rawStatus === "all" ||
        rawStatus === "" ||
        rawStatus == null
    ) {
        queueFilter = QueueFilter.ALL;
    }

    /** @type {Record<string, string>} */
    const columnFilters = {};

    for (const { key } of NOTIFICATION_COLUMN_FILTERS) {
        const raw = searchParams.get(key);

        if (raw != null && String(raw).trim() !== "") {
            const trimmed = String(raw).trim();
            columnFilters[key] = key === "type" ? trimmed.toUpperCase() : trimmed;
        }
    }

    return { sendType, queueFilter, columnFilters };
}

/**
 * Builds a `URLSearchParams` instance representing the given queue filters for the app route (not the notifications API URL).
 *
 * Always sets {@link URL_SEND_TYPE} and {@link URL_STATUS}. `{@link QueueFilter.ALL}` and `null` serialize as `status=all`;
 * `{@link QueueFilter.SENT}`, `{@link QueueFilter.PENDING}`, and `{@link QueueFilter.ERRORS}` serialize as `sent`, `pending`, and `errors` respectively.
 * Only non-empty trimmed column values from {@link NOTIFICATION_COLUMN_FILTERS} keys are appended.
 *
 * @param {string} sendType Send type; one of {@link SendType} values (`automated` | `manual`).
 * @param {string | null} queueFilter Queue segment; one of {@link QueueFilter} values (`null` means ALL).
 * @param {Record<string, string>} columnFilters Active column filters by key.
 * @returns {URLSearchParams} New search params to pass to `setSearchParams` or comparison via `toString()`.
 */
function serializeNotificationsQueueSearchParams(
    sendType,
    queueFilter,
    columnFilters,
) {
    const p = new URLSearchParams();

    p.set(URL_SEND_TYPE, sendType);

    const statusValue =
        queueFilter === null || queueFilter === QueueFilter.ALL
            ? "all"
            : queueFilter;

    p.set(URL_STATUS, statusValue);

    for (const { key } of NOTIFICATION_COLUMN_FILTERS) {
        const value = columnFilters[key];

        if (value != null && String(value).trim() !== "") {
            p.set(key, String(value).trim());
        }
    }

    return p;
}

/**
 * React hook that mirrors notifications queue filters to and from the browser URL (`useSearchParams`).
 *
 * **URL → state:** When the search string changes (navigation, back/forward), {@link parseNotificationsQueueSearchParams}
 * runs in a layout effect so state matches the URL before passive effects run (avoids overwriting history).
 *
 * **State → URL:** When `sendType`, `queueFilter`, or `columnFilters` change, {@link serializeNotificationsQueueSearchParams}
 * updates the location with `replace: true`, skipping updates when the serialized string already matches.
 *
 * Intended for the `/notifications` route; query keys align with {@link NOTIFICATION_COLUMN_FILTERS} and {@link SendType}.
 *
 * @returns {{
 *   sendType: string,
 *   setSendType: import('react').Dispatch<import('react').SetStateAction<string>>,
 *   queueFilter: string | null,
 *   setQueueFilter: import('react').Dispatch<import('react').SetStateAction<string | null>>,
 *   columnFilters: Record<string, string>,
 *   setColumnFilters: import('react').Dispatch<import('react').SetStateAction<Record<string, string>>>,
 * }} Filter state and React setters (same contract as consumed by {@link useNotifications}).
 */
export function useFilterSearchParams() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [sendType, setSendType] = useState(
        () => parseNotificationsQueueSearchParams(searchParams).sendType,
    );
    const [queueFilter, setQueueFilter] = useState(
        () => parseNotificationsQueueSearchParams(searchParams).queueFilter,
    );
    const [columnFilters, setColumnFilters] = useState(
        () => parseNotificationsQueueSearchParams(searchParams).columnFilters,
    );

    /**
     * Browser back/forward or external URL changes → filter state.
     * Layout effect so state matches the URL before the state→URL effect runs (avoids clobbering history).
     */
    useLayoutEffect(() => {
        const parsed = parseNotificationsQueueSearchParams(searchParams);

        if (parsed.sendType !== sendType) {
            setSendType(parsed.sendType);
        }

        if (parsed.queueFilter !== queueFilter) {
            setQueueFilter(parsed.queueFilter);
        }

        if (!columnFiltersEqual(parsed.columnFilters, columnFilters)) {
            setColumnFilters(parsed.columnFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when the route search string changes
    }, [searchParams]);

    /** Filter state → URL (skip when already aligned to avoid replace loops). */
    useEffect(() => {
        const next = serializeNotificationsQueueSearchParams(
            sendType,
            queueFilter,
            columnFilters,
        );

        if (next.toString() === searchParams.toString()) {
            return;
        }

        setSearchParams(next, { replace: true });
    }, [sendType, queueFilter, columnFilters, setSearchParams, searchParams]);

    return {
        sendType,
        setSendType,
        queueFilter,
        setQueueFilter,
        columnFilters,
        setColumnFilters,
    };
}
